import { useCallback, useEffect, useRef } from 'react';
import { isInteractiveElement } from '@bynder-studio/misc';
import { useIsModalOpen } from 'packages/hooks/useIsModalOpen';
import useEditor from 'packages/hooks/useEditor';

export const usePanningShortcuts = () => {
    const { manipulationRenderer } = useEditor();
    const isPanFired = useRef(false);
    const isModalOpen = useIsModalOpen();

    const panHandler = useCallback(() => {
        isPanFired.current = true;
    }, []);

    const togglePanning = useCallback(
        (panMode) => {
            if (!manipulationRenderer || manipulationRenderer.getPanMode() === panMode) {
                return;
            }

            const isPlaying = 'getIsPlaying' in manipulationRenderer ? !manipulationRenderer.getIsPlaying() : false;

            if (panMode && !isPlaying) {
                manipulationRenderer.togglePanMode(panMode);
            } else if (!panMode) {
                manipulationRenderer.togglePanMode(panMode);
            }
        },
        [manipulationRenderer, isModalOpen],
    );

    const handleSpaceBarKeyDown = useCallback(
        (e) => {
            if (
                e.repeat ||
                isInteractiveElement(e) ||
                (e.target.tagName !== 'BODY' && e.target.tagName !== 'CANVAS') ||
                e.keyCode !== 32 ||
                isModalOpen
            ) {
                return;
            }

            isPanFired.current = false;
            togglePanning(true);
        },
        [togglePanning],
    );

    const handleSpaceBarKeyUp = useCallback(
        (e) => {
            if (
                isInteractiveElement(e) ||
                (e.target.tagName !== 'BODY' && e.target.tagName !== 'CANVAS') ||
                e.keyCode !== 32 ||
                isModalOpen
            ) {
                return;
            }

            togglePanning(false);
        },
        [togglePanning],
    );

    useEffect(() => {
        if (!manipulationRenderer) {
            return;
        }

        manipulationRenderer.canvasManipulation.emitter.on('requestCanvasPan', panHandler);

        return () => {
            manipulationRenderer.eventEmitter.off('requestCanvasPan', panHandler);
        };
    }, [manipulationRenderer, handleSpaceBarKeyUp, panHandler]);

    useEffect(() => {
        window.addEventListener('keydown', handleSpaceBarKeyDown);
        window.addEventListener('keyup', handleSpaceBarKeyUp);

        return () => {
            window.removeEventListener('keyup', handleSpaceBarKeyUp);
            window.removeEventListener('keydown', handleSpaceBarKeyDown);
        };
    }, [handleSpaceBarKeyUp, handleSpaceBarKeyDown]);
};
